import { PkButton, PkDivider, PkIcon, PkNumStatItem } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo } from 'react';

import { useWindowSize } from '../../hooks/useWindowSize';
import { useTranslateFn } from '../../Translate';
import VsTeams from '../VsTeams';

import { getStat } from './utils';

import type { Match } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

const HeadToHeadContent = ({
  options: { match, disableTeamLink },
  home,
  away,
  h2hLink = '',
  showMatchH2HCta,
  extendedView,
}: {
  options: { match: Match; disableTeamLink?: boolean };
  home: any;
  away: any;
  h2hLink?: string;
  showMatchH2HCta: boolean;
  extendedView: boolean;
}) => {
  const t = useTranslateFn();
  const { isDesktopSmall } = useWindowSize();

  const appearance = getStat(home, 'matches_appearance');
  const matchesDraw = () => getStat(home, 'matches_draw');
  const matchesWon = (data) => getStat(data, 'matches_win');
  const goalScored = (data) => getStat(data, 'goals');

  const homeBigger = matchesWon(home) > matchesDraw() && matchesWon(home) > matchesWon(away);
  const awayBigger = matchesWon(away) > matchesDraw() && matchesWon(away) > matchesWon(home);
  const drawsBigger = matchesDraw() > matchesWon(home) && matchesDraw() > matchesWon(away);
  const enableLink = !disableTeamLink;

  return (
    <>
      <h3 className={clsx(classes.title, 'card-match-title')}>{t('head_to_head')}</h3>
      {extendedView && !!appearance && (
        <div className={classes.subtitle}>
          {+appearance} {t(appearance > 1 ? 'previous_matches' : 'previous_match')}
        </div>
      )}
      <VsTeams
        className={classes.vsTeam}
        match={match}
        withDivider={!extendedView}
        invertSecondTeam={!extendedView}
        showTeamName={!isDesktopSmall}
        enableLink={enableLink}
      />
      <div
        className={clsx({
          [classes.stats]: true,
          [classes.statsBig]: homeBigger || awayBigger || drawsBigger,
        })}
      >
        {/* WINS TEAM A */}
        <PkNumStatItem className={classes.statsA}>
          <div
            slot='stat-value'
            className={clsx({
              [classes.score]: true,
              [classes.scoreBig]: homeBigger,
            })}
          >
            <span>{matchesWon(home)}</span>
          </div>
          <span slot='stat-label' className={classes.label}>
            {t('wins')}
          </span>
        </PkNumStatItem>
        {/* DRAWS */}
        <PkNumStatItem className={classes.statsDraw}>
          <div
            slot='stat-value'
            className={clsx({
              [classes.score]: true,
              [classes.scoreBig]: drawsBigger,
            })}
          >
            <span>{matchesDraw()}</span>
          </div>
          <span slot='stat-label' className={classes.label}>
            {t('draws_nowinner')}
          </span>
        </PkNumStatItem>
        {/* WINS TEAM B */}
        <PkNumStatItem className={classes.statsB}>
          <div
            slot='stat-value'
            className={clsx({
              [classes.score]: true,
              [classes.scoreBig]: awayBigger,
            })}
          >
            <span>{matchesWon(away)}</span>
          </div>
          <span slot='stat-label' className={classes.label}>
            {t('wins')}
          </span>
        </PkNumStatItem>
      </div>
      {!extendedView && (
        <>
          <PkDivider />
          <div className={clsx(classes.stats, classes.goals)}>
            <div className={classes.stats__a}>
              <div className={classes.score}>
                <span>{goalScored(home)}</span>
              </div>
            </div>
            <div className={classes.stats__draw}>{t('goal scored')}</div>
            <div className={classes.stats__b}>
              <div className={classes.score}>
                <span>{goalScored(away)}</span>
              </div>
            </div>
          </div>
        </>
      )}
      {showMatchH2HCta && !!h2hLink && (
        <PkButton className={classes.link} linkHref={h2hLink} appearance='text'>
          {t('more_details')} <PkIcon name='navigation-chevron-right' />
        </PkButton>
      )}
    </>
  );
};

export default memo(HeadToHeadContent);
